import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy do form de config de moderação (ADR-0471): descritores de campo numérico, tooltips e o
   catálogo de regras do scanner. O header/toasts da PÁGINA ficam em
   `content/sections/moderation-config.ts` (frente de routes/) — este arquivo é só o que
   `components/moderation/` desenha por conta própria. */
export interface ModerationScannerContent {
  readonly severityLabels: {
    readonly error: string;
    readonly warning: string;
    readonly info: string;
  };
  readonly limitFields: {
    readonly maxFindingsLabel: string;
    readonly maxFindingsTooltip: string;
    readonly maxArchiveBytesLabel: string;
    readonly maxArchiveBytesTooltip: string;
    readonly timeoutMsLabel: string;
    readonly timeoutMsTooltip: string;
    readonly scanTimeoutMsLabel: string;
    readonly scanTimeoutMsTooltip: string;
  };
  readonly heuristicFields: {
    readonly dataPayloadMinBase64Label: string;
    readonly dataPayloadMinBase64Tooltip: string;
    readonly maxVendoredFilesLabel: string;
    readonly maxVendoredFilesTooltip: string;
  };
  readonly tooltips: {
    readonly fastpath: string;
    readonly scannerEnabled: string;
    readonly failOn: string;
    readonly rules: string;
  };
  readonly form: {
    readonly fastpathCardTitle: string;
    readonly fastpathCardDescription: string;
    readonly fastpathAriaLabel: string;
    readonly fastpathLabel: string;
    readonly fastpathOnWord: string;
    readonly fastpathOffWord: string;
    readonly fastpathOffWarning: string;
    readonly scannerCardTitle: string;
    readonly scannerCardDescription: string;
    readonly scannerAriaLabel: string;
    readonly scannerLabel: string;
    readonly scannerOnWord: string;
    readonly scannerOffWord: string;
    readonly failOnAriaLabel: string;
    readonly failOnLabel: string;
    readonly limitsCardTitle: string;
    readonly limitsCardDescription: string;
    readonly heuristicsCardTitle: string;
    readonly heuristicsCardDescription: string;
    readonly rulesCardTitle: string;
    readonly rulesCardDescription: string;
    readonly rulesAriaLabel: string;
    readonly rulesLabel: string;
    readonly noRuleDescription: string;
    readonly requiredRuleLabel: string;
    readonly ruleAriaLabel: (title: string) => string;
    readonly fieldAriaLabel: (label: string) => string;
    readonly save: string;
  };
  readonly ruleCatalog: Readonly<
    Record<string, { readonly title: string; readonly description: string }>
  >;
}

const MODERATION_SCANNER_CONTENT: Bilingual<ModerationScannerContent> = {
  'pt-BR': {
    severityLabels: { error: 'Erro', warning: 'Aviso', info: 'Info' },
    limitFields: {
      maxFindingsLabel: 'Máx. findings',
      maxFindingsTooltip:
        'Teto de findings reportados por scan. Acima disso a lista é truncada — não muda o veredito, só limita o tamanho do relatório.',
      maxArchiveBytesLabel: 'Máx. bytes do arquivo',
      maxArchiveBytesTooltip:
        'Tamanho máximo, em bytes, do arquivo .zip do pack aceito no scan. Arquivos maiores são recusados antes da análise.',
      timeoutMsLabel: 'Timeout total (ms)',
      timeoutMsTooltip:
        'Tempo máximo (ms) do processamento do scan em si — decifra e varredura do conteúdo em memória. Estourou, o scan é abortado.',
      scanTimeoutMsLabel: 'Backstop de scan (ms)',
      scanTimeoutMsTooltip:
        'Rede de segurança (ms) para submissão presa no estado "scanning": passado esse tempo ela é destravada. Também define o TTL da URL temporária entregue ao runner.',
    },
    heuristicFields: {
      dataPayloadMinBase64Label: 'Payload mín. base64',
      dataPayloadMinBase64Tooltip:
        'Tamanho mínimo (caracteres) de um trecho base64 contíguo em dados não-código para ser sinalizado como possível payload oculto. Menor = mais sensível.',
      maxVendoredFilesLabel: 'Máx. arquivos vendored',
      maxVendoredFilesTooltip:
        'Acima deste número de arquivos vendados (node_modules/ embutido), a regra "bundled-deps-present" escala de aviso para erro.',
    },
    tooltips: {
      fastpath:
        'Circuit-breaker global. Ligado, packs de publishers verificados que passam no scan são aprovados sem revisão humana. Desligado, todo pack vai para moderação manual.',
      scannerEnabled:
        'Liga o scanner de segurança que analisa o conteúdo do pack. Desligado, os packs não passam pela varredura automática antes da decisão.',
      failOn:
        'Quais severidades de finding reprovam a submissão (status "fail"). Erros sempre bloqueiam; as demais severidades podem ser ajustadas.',
      rules:
        'Regras individuais do scanner. Regras obrigatórias protegem a integridade da análise e não podem ser desligadas.',
    },
    form: {
      fastpathCardTitle: 'Fastpath (auto-aprovação)',
      fastpathCardDescription:
        'Circuit-breaker global. Desligar manda todo pack para review manual, mesmo de publishers verificados.',
      fastpathAriaLabel: 'Sobre o fastpath',
      fastpathLabel: 'Fastpath',
      fastpathOnWord: 'ligado',
      fastpathOffWord: 'desligado',
      fastpathOffWarning:
        'Com o fastpath desligado, nenhum pack é auto-aprovado — todos vão para moderação manual.',
      scannerCardTitle: 'Scanner',
      scannerCardDescription: 'Política do scanner de segurança dos packs.',
      scannerAriaLabel: 'Sobre o scanner',
      scannerLabel: 'Scanner',
      scannerOnWord: 'ligado',
      scannerOffWord: 'desligado',
      failOnAriaLabel: 'Sobre reprovar em',
      failOnLabel: 'Reprovar em',
      limitsCardTitle: 'Limites e timeouts',
      limitsCardDescription: 'Tetos de custo e janelas de tempo do scan.',
      heuristicsCardTitle: 'Heurísticas de detecção',
      heuristicsCardDescription: 'Limiares que decidem quando uma regra escala a severidade.',
      rulesCardTitle: 'Regras',
      rulesCardDescription: 'Habilitação por regra do scanner.',
      rulesAriaLabel: 'Sobre as regras',
      rulesLabel: 'Regras',
      noRuleDescription: 'Regra sem descrição no catálogo.',
      requiredRuleLabel: 'Obrigatória',
      ruleAriaLabel: (title) => `Regra ${title}`,
      fieldAriaLabel: (label) => `Sobre ${label}`,
      save: 'Salvar',
    },
    ruleCatalog: {
      'eval-call': {
        title: 'Uso de eval()',
        description:
          'Detecta chamadas a eval(), que executam código arbitrário montado em runtime. Um generator legítimo nunca precisa disso — é a rota clássica de execução maliciosa.',
      },
      'new-function': {
        title: 'Construtor Function()',
        description:
          'Detecta new Function()/Function(), que cria uma função a partir de uma string — equivale a eval. Reprova pelo mesmo motivo: código dinâmico que escapa à análise.',
      },
      'dynamic-code-reference': {
        title: 'Referência a código dinâmico',
        description:
          'Reprova aliases e referências indiretas a eval, Function ou constructor.constructor que poderiam ocultar execução de código montado em runtime.',
      },
      'process-builtin-loader': {
        title: 'Carregador nativo de módulos',
        description:
          'Reprova process.getBuiltinModule(), inclusive por aliases, porque ele carrega módulos nativos sem imports estáticos auditáveis.',
      },
      'process-native-escape': {
        title: 'Escape nativo via process',
        description:
          'Reprova APIs como process.binding(), dlopen e mainModule, que contornam a fronteira de módulos analisada pelo scanner.',
      },
      'vm-code-loading': {
        title: 'Execução via node:vm',
        description:
          'Reprova importação de node:vm, capaz de executar código construído em runtime fora da análise estática.',
      },
      'child-process-import': {
        title: 'Importa child_process',
        description:
          'Detecta import/require de child_process, que permite executar comandos de shell. Reprova a menos que a capacidade seja declarada em stack.requires (ex.: cli.<tool>).',
      },
      'dynamic-require': {
        title: 'require() dinâmico',
        description:
          'Reprova require() com argumento não-literal. O módulo carregado dinamicamente escapa à análise estática e pode esconder um import perigoso.',
      },
      'dynamic-import': {
        title: 'import() dinâmico',
        description:
          'Reprova import() com especificador não-literal no generator. No conteúdo base, a exceção exige capability explícita no manifesto.',
      },
      'fs-write-arbitrary': {
        title: 'Escrita via fs cru',
        description:
          'Detecta escrita/remoção direta pelo fs (writeFile/rm/unlink). Reprova porque bypassa as APIs seguras do framework (path-safe) e pode tocar arquivos fora do escopo do pack.',
      },
      'net-connect-unallowed': {
        title: 'Acesso de rede',
        description:
          'Detecta acesso de rede (net/http/https/fetch). O generator deveria rodar offline — rede pode exfiltrar dados ou baixar payload. Só passa se network for declarado em stack.requires.',
      },
      'process-env-secrets': {
        title: 'Leitura de segredos do ambiente',
        description:
          'Detecta leitura de segredos via process.env (SECRET/TOKEN/API_KEY/PASSWORD/…). O generator não deveria acessar credenciais do ambiente.',
      },
      'crypto-weak-random': {
        title: 'PRNG fraco (Math.random)',
        description:
          'Detecta uso de Math.random(), um gerador pseudoaleatório fraco e previsível — inseguro para tokens ou segredos. Sinaliza para usar crypto.randomUUID()/randomBytes().',
      },
      'obfuscation-suspect': {
        title: 'Código ofuscado/não parseável',
        description:
          'Reprova arquivos que não puderam ser parseados. Sem uma AST válida, o scanner não consegue comprovar que o código é seguro.',
      },
      'data-embedded-payload': {
        title: 'Payload em arquivo de dados',
        description:
          'Detecta payload escondido em arquivos não-código (blob base64 longo, URI data:/javascript:, URL externa). O scanner de código é cego a dados que podem ser carregados em runtime.',
      },
      'bundled-deps-present': {
        title: 'Dependências embutidas',
        description:
          'Detecta node_modules/ embutido no pack (deps vendadas), que o scanner não audita a fundo. Escala de aviso para erro acima do limite de arquivos vendados.',
      },
      'dataflow-secret-exfiltration': {
        title: 'Exfiltração de credencial (taint)',
        description:
          'Análise de fluxo que conecta um segredo de env (process.env) a um sink de rede (fetch/http). É o padrão clássico de exfiltração de credencial — exige revisão humana.',
      },
    },
  },
  'en-US': {
    severityLabels: { error: 'Error', warning: 'Warning', info: 'Info' },
    limitFields: {
      maxFindingsLabel: 'Max findings',
      maxFindingsTooltip:
        'Ceiling on findings reported per scan. Above this the list is truncated — it does not change the verdict, only limits the report size.',
      maxArchiveBytesLabel: 'Max archive bytes',
      maxArchiveBytesTooltip:
        'Maximum size, in bytes, of the pack .zip accepted in the scan. Larger files are rejected before analysis.',
      timeoutMsLabel: 'Total timeout (ms)',
      timeoutMsTooltip:
        'Maximum time (ms) for the scan processing itself — decoding and in-memory content sweep. If exceeded, the scan is aborted.',
      scanTimeoutMsLabel: 'Scan backstop (ms)',
      scanTimeoutMsTooltip:
        'Safety net (ms) for a submission stuck in the "scanning" state: after this time it is released. Also sets the TTL of the temporary URL handed to the runner.',
    },
    heuristicFields: {
      dataPayloadMinBase64Label: 'Min base64 payload',
      dataPayloadMinBase64Tooltip:
        'Minimum size (characters) of a contiguous base64 chunk in non-code data to be flagged as a possible hidden payload. Lower = more sensitive.',
      maxVendoredFilesLabel: 'Max vendored files',
      maxVendoredFilesTooltip:
        'Above this number of vendored files (bundled node_modules/), the "bundled-deps-present" rule escalates from warning to error.',
    },
    tooltips: {
      fastpath:
        'Global circuit-breaker. On, packs from verified publishers that pass the scan are approved without human review. Off, every pack goes to manual moderation.',
      scannerEnabled:
        'Turns on the security scanner that analyzes pack content. Off, packs do not go through automatic scanning before the decision.',
      failOn:
        'Which finding severities fail the submission (status "fail"). Errors always block; other severities remain configurable.',
      rules:
        'Individual scanner rules. Required rules protect analysis integrity and cannot be turned off.',
    },
    form: {
      fastpathCardTitle: 'Fastpath (auto-approval)',
      fastpathCardDescription:
        'Global circuit-breaker. Turning it off sends every pack to manual review, even from verified publishers.',
      fastpathAriaLabel: 'About fastpath',
      fastpathLabel: 'Fastpath',
      fastpathOnWord: 'on',
      fastpathOffWord: 'off',
      fastpathOffWarning:
        'With fastpath off, no pack is auto-approved — all go to manual moderation.',
      scannerCardTitle: 'Scanner',
      scannerCardDescription: 'Security scanner policy for packs.',
      scannerAriaLabel: 'About the scanner',
      scannerLabel: 'Scanner',
      scannerOnWord: 'on',
      scannerOffWord: 'off',
      failOnAriaLabel: 'About fail on',
      failOnLabel: 'Fail on',
      limitsCardTitle: 'Limits and timeouts',
      limitsCardDescription: 'Cost ceilings and scan time windows.',
      heuristicsCardTitle: 'Detection heuristics',
      heuristicsCardDescription: 'Thresholds that decide when a rule escalates severity.',
      rulesCardTitle: 'Rules',
      rulesCardDescription: 'Per-rule scanner enablement.',
      rulesAriaLabel: 'About the rules',
      rulesLabel: 'Rules',
      noRuleDescription: 'Rule with no description in the catalog.',
      requiredRuleLabel: 'Required',
      ruleAriaLabel: (title) => `Rule ${title}`,
      fieldAriaLabel: (label) => `About ${label}`,
      save: 'Save',
    },
    ruleCatalog: {
      'eval-call': {
        title: 'Use of eval()',
        description:
          'Detects calls to eval(), which execute arbitrary code built at runtime. A legitimate generator never needs this — it is the classic route for malicious execution.',
      },
      'new-function': {
        title: 'Function() constructor',
        description:
          'Detects new Function()/Function(), which creates a function from a string — equivalent to eval. Fails for the same reason: dynamic code that escapes analysis.',
      },
      'dynamic-code-reference': {
        title: 'Dynamic code reference',
        description:
          'Fails aliases and indirect references to eval, Function, or constructor.constructor that could hide runtime-generated code execution.',
      },
      'process-builtin-loader': {
        title: 'Native module loader',
        description:
          'Fails process.getBuiltinModule(), including aliases, because it loads native modules without auditable static imports.',
      },
      'process-native-escape': {
        title: 'Native process escape',
        description:
          'Fails APIs such as process.binding(), dlopen, and mainModule, which bypass the module boundary analyzed by the scanner.',
      },
      'vm-code-loading': {
        title: 'Execution through node:vm',
        description:
          'Fails imports of node:vm, which can execute runtime-generated code outside static analysis.',
      },
      'child-process-import': {
        title: 'Imports child_process',
        description:
          'Detects import/require of child_process, which allows running shell commands. Fails unless the capability is declared in stack.requires (e.g. cli.<tool>).',
      },
      'dynamic-require': {
        title: 'Dynamic require()',
        description:
          'Fails require() with a non-literal argument. The dynamically loaded module escapes static analysis and may hide a dangerous import.',
      },
      'dynamic-import': {
        title: 'Dynamic import()',
        description:
          'Fails import() with a non-literal specifier in the generator. Base content requires an explicit manifest capability for its exception.',
      },
      'fs-write-arbitrary': {
        title: 'Raw fs write',
        description:
          'Detects direct write/removal via fs (writeFile/rm/unlink). Fails because it bypasses the framework safe APIs (path-safe) and may touch files outside the pack scope.',
      },
      'net-connect-unallowed': {
        title: 'Network access',
        description:
          'Detects network access (net/http/https/fetch). The generator should run offline — network access can exfiltrate data or download a payload. Only passes if network is declared in stack.requires.',
      },
      'process-env-secrets': {
        title: 'Reads environment secrets',
        description:
          'Detects reading secrets via process.env (SECRET/TOKEN/API_KEY/PASSWORD/…). The generator should not access environment credentials.',
      },
      'crypto-weak-random': {
        title: 'Weak PRNG (Math.random)',
        description:
          'Detects use of Math.random(), a weak, predictable pseudo-random generator — unsafe for tokens or secrets. Flags to use crypto.randomUUID()/randomBytes() instead.',
      },
      'obfuscation-suspect': {
        title: 'Obfuscated/unparseable code',
        description:
          'Fails files that could not be parsed. Without a valid AST, the scanner cannot prove that the code is safe.',
      },
      'data-embedded-payload': {
        title: 'Payload in data file',
        description:
          'Detects a hidden payload in non-code files (long base64 blob, data:/javascript: URI, external URL). The code scanner is blind to data that may be loaded at runtime.',
      },
      'bundled-deps-present': {
        title: 'Bundled dependencies',
        description:
          'Detects a bundled node_modules/ inside the pack (vendored deps), which the scanner does not audit deeply. Escalates from warning to error above the vendored file limit.',
      },
      'dataflow-secret-exfiltration': {
        title: 'Credential exfiltration (taint)',
        description:
          'Flow analysis that connects an env secret (process.env) to a network sink (fetch/http). This is the classic credential exfiltration pattern — requires human review.',
      },
    },
  },
};

export const useModerationScannerContent = (): ModerationScannerContent =>
  useBilingual(MODERATION_SCANNER_CONTENT);
